import { devThrowError } from '@ecomm/error-handling'
import { voidFn } from '@simplisafe/ewok'
import classNames from 'classnames'
import React from 'react'
import { Helmet } from 'react-helmet'
import { addSrc, makeFormat } from './utils'
type ImageLoading = Record<
  string,
  React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  >
>

const imageLoading: ImageLoading = {
  high: {
    fetchPriority: 'high',
    decoding: 'sync'
  },
  normal: {},
  lazy: {
    decoding: 'async',
    loading: 'lazy'
  }
}

type Props = {
  readonly url: string
  /** ONLY 1 image per page can have a high loading prop */
  readonly loading?: 'high' | 'lazy' | 'normal'
  readonly quality?: number
  readonly width?: number
  readonly height?: number
  readonly description: string
  readonly className?: string
  readonly classNameOverride?: string
  readonly originalWidth: number
  readonly originalHeight: number
  readonly format?: 'unset' | 'webp'
  readonly onClick?: (element: React.MouseEvent<HTMLImageElement>) => void
  readonly style?: React.CSSProperties
  readonly preload?: boolean
  readonly presentationOnly?: boolean
}

/**
 * [Uses Contentful's Images API](https://www.contentful.com/developers/docs/references/images-api/) to request an
 * image of the correct size, format, and quality.
 *
 * Width and height are the maximum dimensions of the image.
 * You will generally only need to pass the width or height to an image since
 * the image will be resized to fit within these dimensions while maintaining its aspect ratio based on the original height and width.
 *
 * When providing a width or height you should always provide dimensions that match with the container the image will be in.
 * If you have a div with a max width of 525px, then the image should have a width set to 525.
 *
 * `srcSet` is used to automatically load higher quality images when the user's device supports it,
 * so providing larger numbers will often result in large file sizes being loaded.
 *
 * An image with a 525 width will size up on for an iphone 13 to 3x it's size, so the largest image will be 1575px wide.
 *
 * When adding images keep an eye on the network tab. Images should never be larger than 20 kB.
 * If an image is larger than 20 kB, consider reducing the quality or dimensions.
 *
 * Prominent images above the fold should be loaded eagerly.
 *
 * [Uses HTML's `loading` attribute to lazy load images by default.](https://developer.mozilla.org/en-US/docs/Web/Performance/Lazy_loading)
 *
 *
 * @param url The URL of the image to request. In a GraphQL query this is the assets URL.
 * @param loading The loading attribute for the image. Defaults to 'lazy'.
 * @param quality The quality of the image. The value should not exceed 90. Defaults to 50.
 * @param width The width of the image. The maximum width should not exceed 1400px, as it may lead to error when fetching from Contentful for devices with high pixel density.
 * @param height The height of the image.
 * @param description The alt text of the image. It is ignored if the `presentationOnly` prop is set to true.
 * @param className Adds to the default className.
 * @param classNameOverride Overrides the default className.
 * @param originalWidth The original width of the image. Used for calculating aspect ratio.
 * @param originalHeight The original height of the image. Used for calculating aspect ratio.
 * @param format The format of the final image. By default, if the value is not passed, it'll turn the image to a webp for better performance. But if the value is "unset", it will get the default extension of the image
 * @param presentationOnly Whether the image should be hidden from assistive technologies. Use only if the image conveys no additional information to the user.
 */
export const ContentfulImage = React.memo(function ({
  url: _url,
  loading = 'lazy',
  height = 800,
  width = 800,
  quality = 50,
  description,
  className = '',
  classNameOverride = '',
  originalWidth,
  originalHeight,
  format = 'webp',
  onClick = voidFn,
  style = {},
  preload = false,
  presentationOnly = false
}: Props) {
  const url = _url.replace(
    'https://downloads.ctfassets.net',
    'https://images.ctfassets.net'
  )

  const finalFormat = makeFormat(format)

  quality > 90 &&
    devThrowError(Error('ContentfulImage: quality must not exceed 90'))

  const srcSet = `
      ${url}?w=${Math.round(width)}&h=${Math.round(height)}&q=${Math.round(
        quality
      )}${finalFormat} 1x,
        ${url}?w=${Math.round(width * 1.25)}&h=${Math.round(
          height * 1.25
        )}&q=${Math.round(quality)}${finalFormat} 1.5x,
        ${url}?w=${Math.round(width * 1.75)}&h=${Math.round(
          height * 1.75
        )}&q=${Math.round(quality)}${finalFormat} 2x,
        ${url}?w=${Math.round(width * 2.25)}&h=${Math.round(
          height * 2.25
        )}&q=${Math.round(quality)}${finalFormat} 2.5x,
        ${url}?w=${Math.round(width * 2.75)}&h=${Math.round(
          height * 2.75
        )}&q=${Math.round(quality)}${finalFormat} 3x,
      `.trim() // remove extra spaces

  const src = `${url}?w=${Math.round(width)}&h=${Math.round(height)}&q=${Math.round(quality)}${finalFormat}`

  return (
    <>
      {preload ? (
        <Helmet>
          <link as="img" href={src} imageSrcSet={srcSet} rel="preload" />
        </Helmet>
      ) : null}
      <img
        alt={!presentationOnly ? description : ''}
        className={classNames([
          classNameOverride === ''
            ? `bg-neutral-light-50 w-full ${className}`
            : classNameOverride
        ])}
        data-testid="contentful-image"
        data-component="contentful-image"
        height="auto"
        onClick={onClick}
        ref={addSrc(src)}
        srcSet={srcSet}
        style={{
          objectFit: 'cover',
          aspectRatio: `${Math.round(originalWidth)}/${Math.round(
            originalHeight
          )}`,
          ...style
        }}
        width="auto"
        {...imageLoading[loading]}
        role={presentationOnly ? 'presentation' : undefined}
      />
    </>
  )
})
